import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getToken, setToken } from './api/client';

const AuthContext = createContext(null);
const API = '/api';
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  // No localStorage restore — a page refresh always starts logged out.
  const [user, setUser] = useState(null);
  const inactivityTimer = useRef(null);

  const logout = useCallback(async () => {
    const token = getToken();
    if (token) {
      try { await fetch(API + '/auth/logout', { method: 'POST', headers: { Authorization: 'Bearer ' + token } }); } catch(e) {}
    }
    setToken(null);
    setUser(null);
  }, []);

  // Heartbeat every 2 minutes (server-side last_seen tracking)
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      const token = getToken();
      if (token) fetch(API + '/auth/heartbeat', { method: 'POST', headers: { Authorization: 'Bearer ' + token } }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 120000);
    return () => clearInterval(t);
  }, [user]);

  // 30-minute inactivity auto-logout
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => { logout(); }, INACTIVITY_LIMIT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [user, logout]);

  const login = (token, username, role, workspace, exportsEnabled = true) => {
    setToken(token);
    setUser({ username, role, workspace, exportsEnabled });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
